import { useContext, useEffect, useMemo, useState } from "react";
import {
  Timestamp,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { AuthContext } from "../AuthContext";

const RANGE_OPTIONS = [
  { value: "7", label: "Last 7 days", days: 7 },
  { value: "30", label: "Last 30 days", days: 30 },
  { value: "90", label: "Last 90 days", days: 90 },
  { value: "all", label: "All time", days: null },
];

const MAX_EVENTS = 1000;
const SAMPLE_STUDENTS = 30;
const SAMPLE_DAYS = 30;
const SAMPLE_EVENTS_MIN = 40;
const SAMPLE_EVENTS_MAX = 70;

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value.seconds) return value.seconds * 1000;
  return 0;
};

const toIso = (value) => {
  if (!value) return "";
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  if (value.seconds) return new Date(value.seconds * 1000).toISOString();
  return "";
};

const escapeCsv = (value) => {
  const text = String(value ?? "");
  if (text.includes("\"") || text.includes(",") || text.includes("\n")) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const sanitizeValue = (value) => {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(sanitizeValue);
  if (typeof value === "object") {
    if (typeof value.toDate === "function") {
      return value.toDate().toISOString();
    }
    if (value.seconds && typeof value.seconds === "number") {
      return new Date(value.seconds * 1000).toISOString();
    }
    return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, sanitizeValue(val)]));
  }
  return value;
};

const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const randomPick = (items) => items[Math.floor(Math.random() * items.length)];

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

export default function AdminAnalytics() {
  const { isAdmin, loading: authLoading, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [range, setRange] = useState("30");
  const [sampleStatus, setSampleStatus] = useState("");
  const [sampleError, setSampleError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  const [exportError, setExportError] = useState("");

  useEffect(() => {
    if (authLoading || !isAdmin) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const q = query(
          collection(db, "analyticsEvents"),
          orderBy("createdAt", "desc"),
          limit(MAX_EVENTS)
        );
        const snap = await getDocs(q);
        setItems(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (err) {
        console.error(err);
        setError("Failed to load analytics events.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authLoading, isAdmin]);

  const filteredItems = useMemo(() => {
    const option = RANGE_OPTIONS.find((item) => item.value === range);
    if (!option || !option.days) return items;

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - option.days);
    const cutoffMs = cutoff.getTime();

    return items.filter((item) => toMillis(item.createdAt) >= cutoffMs);
  }, [items, range]);

  const totals = useMemo(() => {
    let pageViews = 0;
    let actions = 0;
    const users = new Set();

    filteredItems.forEach((item) => {
      if (item.type === "page_view") {
        pageViews += 1;
      } else {
        actions += 1;
      }

      if (item.userId) {
        users.add(item.userId);
      }
    });

    return {
      total: filteredItems.length,
      pageViews,
      actions,
      users: users.size,
    };
  }, [filteredItems]);

  const routeCounts = useMemo(() => {
    const map = {};
    filteredItems.forEach((item) => {
      if (item.type !== "page_view") return;
      const key = item.name || "unknown";
      map[key] = (map[key] || 0) + 1;
    });

    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredItems]);

  const actionCounts = useMemo(() => {
    const map = {};
    filteredItems.forEach((item) => {
      if (item.type === "page_view") return;
      const key = item.type || "unknown";
      map[key] = (map[key] || 0) + 1;
    });

    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredItems]);

  const exportCsv = () => {
    const headers = ["time", "type", "name", "userId", "classId", "meta"];
    const rows = filteredItems.map((item) => [
      toIso(item.createdAt),
      item.type || "",
      item.name || "",
      item.userId || "",
      item.classId || "",
      item.meta ? JSON.stringify(item.meta) : "",
    ]);

    const csv = [headers.join(","), ...rows.map((row) => row.map(escapeCsv).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `analytics-events-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const exportAllData = async () => {
    setExporting(true);
    setExportStatus("Preparing export...");
    setExportError("");

    try {
      const classesSnap = await getDocs(collection(db, "classes"));
      const classDocs = classesSnap.docs.map((snap) => ({ id: snap.id, data: snap.data() }));

      const classes = await Promise.all(
        classDocs.map(async (cls) => {
          const basePath = ["classes", cls.id];
          const [
            studentsSnap,
            eventsSnap,
            objectivesSnap,
            attendanceSnap,
            rollupsSnap,
            deskLayoutsSnap,
            seatingPlanSnap,
          ] = await Promise.all([
            getDocs(collection(db, ...basePath, "students")),
            getDocs(collection(db, ...basePath, "events")),
            getDocs(collection(db, ...basePath, "objectives")),
            getDocs(collection(db, ...basePath, "attendance")),
            getDocs(collection(db, ...basePath, "dailyRollups")),
            getDocs(collection(db, ...basePath, "deskLayouts")),
            getDocs(collection(db, ...basePath, "seatingPlan")),
          ]);

          return {
            id: cls.id,
            data: cls.data,
            students: studentsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
            events: eventsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
            objectives: objectivesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
            attendance: attendanceSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
            dailyRollups: rollupsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
            deskLayouts: deskLayoutsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
            seatingPlan: seatingPlanSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
          };
        })
      );

      const payload = sanitizeValue({
        exportedAt: new Date().toISOString(),
        classes,
      });

      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: "application/json;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `pilot-backup-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setExportStatus(`Export complete: ${classes.length} class(es)`);
    } catch (err) {
      console.error(err);
      setExportError("Failed to export data. Check Firestore rules for class access.");
    } finally {
      setExporting(false);
    }
  };

  const generateSampleData = async () => {
    if (!user?.uid) {
      setSampleError("You must be logged in to generate sample data.");
      return;
    }

    setGenerating(true);
    setSampleError("");
    setSampleStatus("Generating sample class data...");

    try {
      const classRef = doc(collection(db, "classes"));
      const className = `Sample Class ${new Date().toLocaleDateString()}`;

      await setDoc(classRef, {
        name: className,
        teacherId: user.uid,
        createdAt: serverTimestamp(),
      });

      const studentRefs = [];
      const studentBatch = writeBatch(db);
      for (let i = 0; i < SAMPLE_STUDENTS; i += 1) {
        const studentRef = doc(collection(classRef, "students"));
        studentRefs.push(studentRef.id);
        studentBatch.set(studentRef, { name: `Student ${i + 1}` });
      }
      await studentBatch.commit();

      const objectives = [
        { title: "Focus and attention", category: "Behaviour" },
        { title: "Complete task", category: "Work" },
        { title: "Collaboration", category: "Teamwork" },
        { title: "Quality writing", category: "English" },
        { title: "Problem solving", category: "Maths" },
      ];

      const objectiveIds = [];
      const objectiveBatch = writeBatch(db);
      objectives.forEach((objective) => {
        const ref = doc(collection(classRef, "objectives"));
        objectiveIds.push(ref.id);
        objectiveBatch.set(ref, { ...objective, archived: false });
      });
      await objectiveBatch.commit();

      const startDate = addDays(new Date(), -SAMPLE_DAYS + 1);

      for (let dayIndex = 0; dayIndex < SAMPLE_DAYS; dayIndex += 1) {
        const date = addDays(startDate, dayIndex);
        const dateKey = date.toISOString().slice(0, 10);
        const eventsToday = randomInt(SAMPLE_EVENTS_MIN, SAMPLE_EVENTS_MAX);

        const rollup = {
          date: dateKey,
          positive: 0,
          negative: 0,
          objective: 0,
          objectivesById: {},
          updatedAt: serverTimestamp(),
        };

        const batch = writeBatch(db);
        const rollupRef = doc(collection(classRef, "dailyRollups"), dateKey);

        for (let i = 0; i < eventsToday; i += 1) {
          const roll = Math.random();
          let type = "positive";
          if (roll > 0.65 && roll <= 0.85) type = "negative";
          if (roll > 0.85) type = "objective";

          const studentCount = randomInt(1, 3);
          const shuffled = studentRefs.slice().sort(() => 0.5 - Math.random());
          const studentIds = shuffled.slice(0, studentCount);

          const eventTime = new Date(date);
          eventTime.setHours(randomInt(8, 15), randomInt(0, 59), randomInt(0, 59), 0);

          const event = {
            type,
            studentIds,
            timestamp: Timestamp.fromDate(eventTime),
          };

          if (type === "objective") {
            event.objectiveId = randomPick(objectiveIds);
          }

          const eventRef = doc(collection(classRef, "events"));
          batch.set(eventRef, event);

          if (type === "positive") rollup.positive += 1;
          if (type === "negative") rollup.negative += 1;
          if (type === "objective") {
            rollup.objective += 1;
            if (event.objectiveId) {
              rollup.objectivesById[event.objectiveId] =
                (rollup.objectivesById[event.objectiveId] || 0) + 1;
            }
          }
        }

        const presentCount = Math.max(1, Math.round(SAMPLE_STUDENTS * (0.85 + Math.random() * 0.1)));
        const presentIds = studentRefs.slice().sort(() => 0.5 - Math.random()).slice(0, presentCount);
        const absentIds = studentRefs.filter((id) => !presentIds.includes(id));

        const attendanceRef = doc(collection(classRef, "attendance"));
        batch.set(attendanceRef, {
          date: dateKey,
          presentStudentIds: presentIds,
          absentStudentIds: absentIds,
          timestamp: serverTimestamp(),
        });

        batch.set(rollupRef, rollup, { merge: true });
        await batch.commit();
      }

      setSampleStatus(`Sample data generated: ${className}`);
    } catch (err) {
      console.error(err);
      setSampleError("Failed to generate sample data. Check Firestore rules for dailyRollups.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ padding: "1.5rem" }}>
      <h1>Admin Analytics</h1>
      {authLoading ? (
        <p>Loading...</p>
      ) : !isAdmin ? (
        <p>You do not have access to this page.</p>
      ) : loading ? (
        <p>Loading analytics…</p>
      ) : error ? (
        <p style={{ color: "#b00020" }}>{error}</p>
      ) : (
        <>
          <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "inline-flex", gap: "0.5rem", alignItems: "center" }}>
              Range
              <select value={range} onChange={(event) => setRange(event.target.value)}>
                {RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <span style={{ color: "#555" }}>Showing {filteredItems.length} events</span>
            <button type="button" onClick={exportCsv} disabled={filteredItems.length === 0}>
              Export CSV
            </button>
            <button type="button" onClick={exportAllData} disabled={exporting}>
              {exporting ? "Exporting backup..." : "Export all data (JSON)"}
            </button>
            <button type="button" onClick={generateSampleData} disabled={generating}>
              {generating ? "Generating sample data..." : "Generate sample data"}
            </button>
          </div>
          {exportStatus && <p style={{ color: "#1b5e20" }}>{exportStatus}</p>}
          {exportError && <p style={{ color: "#b00020" }}>{exportError}</p>}
          {sampleStatus && <p style={{ color: "#1b5e20" }}>{sampleStatus}</p>}
          {sampleError && <p style={{ color: "#b00020" }}>{sampleError}</p>}

          <section style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
            <SummaryCard title="Total events" value={totals.total} />
            <SummaryCard title="Page views" value={totals.pageViews} />
            <SummaryCard title="Action events" value={totals.actions} />
            <SummaryCard title="Unique users" value={totals.users} />
          </section>

          <section style={{ marginBottom: "1.5rem" }}>
            <h2>Top pages</h2>
            {routeCounts.length === 0 ? (
              <p>No page views in this range.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Route</th>
                    <th style={thStyle}>Views</th>
                  </tr>
                </thead>
                <tbody>
                  {routeCounts.slice(0, 10).map((row) => (
                    <tr key={row.name}>
                      <td style={tdStyle}>{row.name}</td>
                      <td style={tdStyle}>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section style={{ marginBottom: "1.5rem" }}>
            <h2>Top actions</h2>
            {actionCounts.length === 0 ? (
              <p>No actions in this range.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Action</th>
                    <th style={thStyle}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {actionCounts.map((row) => (
                    <tr key={row.name}>
                      <td style={tdStyle}>{row.name}</td>
                      <td style={tdStyle}>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h2>Recent events</h2>
            {filteredItems.length === 0 ? (
              <p>No events in this range.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Time</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>User</th>
                    <th style={thStyle}>Class</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.slice(0, 25).map((item) => (
                    <tr key={item.id}>
                      <td style={tdStyle}>
                        {item.createdAt?.toDate
                          ? item.createdAt.toDate().toLocaleString()
                          : "–"}
                      </td>
                      <td style={tdStyle}>{item.type || "–"}</td>
                      <td style={tdStyle}>{item.name || "–"}</td>
                      <td style={tdStyle}>{item.userId || "–"}</td>
                      <td style={tdStyle}>{item.classId || "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  borderBottom: "1px solid #ddd",
  padding: "0.5rem",
};

const tdStyle = {
  borderBottom: "1px solid #eee",
  padding: "0.5rem",
  verticalAlign: "top",
};

const SummaryCard = ({ title, value }) => (
  <div
    style={{
      minWidth: "160px",
      padding: "0.75rem 1rem",
      borderRadius: "0.5rem",
      border: "1px solid #ddd",
      background: "#fafafa",
    }}
  >
    <div style={{ fontSize: "0.85rem", color: "#555" }}>{title}</div>
    <div style={{ fontSize: "1.4rem", fontWeight: 600 }}>{value}</div>
  </div>
);
